import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SeoJsonLd from '@/components/base/SeoJsonLd';
import { mainSiteUrl, categories, externalLinkAttrs } from '@/mocks/categories';
import { getArticleBySlug, allArticles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://sugargoospreadsheet.team';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = slug ? getArticleBySlug(slug) : undefined;

  useEffect(() => {
    if (article) {
      document.title = `${article.title} | Sugargoo Spreadsheet`;
    }
  }, [article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 pb-16">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h1 className="font-heading text-4xl font-bold text-foreground-950 mb-4">Article Not Found</h1>
            <p className="text-foreground-600 mb-8">The article you are looking for does not exist or may have been moved.</p>
            <Link to="/" className="px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap">
              Back to Homepage
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const { title, excerpt, date, author, sections, imageUrl } = article;
  const relatedArticles = allArticles.filter(a => a.slug !== article.slug).slice(0, 4);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description: excerpt,
    datePublished: date,
    author: {
      '@type': 'Organization',
      name: author,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Sugargoo Spreadsheet',
      url: siteUrl,
    },
    image: imageUrl,
    url: `${siteUrl}/article/${article.slug}`,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteUrl}/article/${article.slug}`,
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <SeoJsonLd data={articleJsonLd} />
      <Navbar />

      <main>
        {/* ... existing code ... */}
        <section className="relative w-full pt-24 pb-16 md:pt-32 md:pb-20 bg-background-50">
          <div className="w-full px-6 md:px-10 max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row lg:items-start gap-10 lg:gap-20">
              <div className="lg:w-1/2">
                <Link
                  to="/"
                  className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-800 transition-colors mb-6 whitespace-nowrap"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-left-line"></i>
                  </span>
                  Back to Home
                </Link>

                <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-foreground-950 leading-tight">
                  {title}
                </h1>

                <div className="mt-4 flex items-center gap-3 text-xs text-foreground-400">
                  <span>{date}</span>
                  <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                  <span>{author}</span>
                  <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                  <span>8 min read</span>
                </div>
              </div>

              <div className="lg:w-1/2">
                <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
                  {excerpt}
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link
                    to="/"
                    className="px-4 py-2 rounded-full bg-background-100 text-sm text-foreground-600 hover:bg-background-200 transition-colors whitespace-nowrap"
                  >
                    ← Back to Home
                  </Link>
                  <a
                    href={mainSiteUrl}
                    {...externalLinkAttrs}
                    className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    Visit Main Website
                  </a>
                </div>
              </div>
            </div>

            <div className="mt-12 rounded-2xl overflow-hidden h-[300px] md:h-[400px]">
              <img
                src={imageUrl}
                alt={title}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </section>

        <section className="w-full px-6 md:px-10 py-16 bg-background-50">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-block px-4 py-1.5 rounded-full border border-foreground-200/60 text-xs font-medium text-foreground-500 tracking-wide uppercase">
              In-Depth Guide
            </span>
            <div className="mt-8 space-y-3">
              <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-950 leading-tight">
                Everything you need
                <span className="inline-flex items-center gap-2 mx-2 align-middle">
                  <span className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-accent-100 rounded-lg">
                    <i className="ri-file-list-3-line text-xl md:text-2xl text-accent-600"></i>
                  </span>
                </span>
                to know
              </h2>
              <h2 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-950 leading-tight">
                about
                <span className="inline-flex items-center gap-2 mx-2 align-middle">
                  <span className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-secondary-100 rounded-lg">
                    <i className="ri-shopping-cart-2-line text-xl md:text-2xl text-secondary-600"></i>
                  </span>
                </span>
                Sugargoo shopping
              </h2>
            </div>
          </div>
        </section>

        {sections.some(s => !s.isFaq) && (
          <section className="w-full px-6 md:px-10 py-12 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-10">
                <span className="inline-block px-4 py-1.5 rounded-full border border-foreground-200/60 text-xs font-medium text-foreground-500 tracking-wide uppercase">
                  In This Guide
                </span>
                <h2 className="mt-4 font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  What You Will Learn
                </h2>
                <p className="mt-2 text-sm text-foreground-500">
                  Click any section to jump to detailed breakdowns
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {sections.filter(s => !s.isFaq).slice(0, 4).map((section) => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className="group flex flex-col p-5 rounded-lg bg-background-50 border border-background-200/60 hover:bg-accent-500 hover:border-accent-500 transition-all duration-300"
                  >
                    <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-100 group-hover:bg-white/20 transition-colors">
                      <i className="ri-arrow-right-line text-lg text-foreground-600 group-hover:text-white transition-colors"></i>
                    </span>
                    <h3 className="mt-3 text-sm font-semibold text-foreground-950 group-hover:text-white transition-colors">
                      {section.title}
                    </h3>
                    <p className="mt-2 text-xs text-foreground-500 group-hover:text-white/80 transition-colors line-clamp-2">
                      {section.content.substring(0, 100)}...
                    </p>
                  </a>
                ))}
              </div>
            </div>
          </section>
        )}

        {sections.map((section) => (
          <section
            key={section.id}
            id={section.id}
            className="w-full px-6 md:px-10 py-14 md:py-16 bg-background-50 even:bg-background-100"
          >
            <div className="max-w-3xl mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
                {section.title}
              </h2>

              {section.isFaq && section.faqs ? (
                <div className="space-y-6">
                  {section.faqs.map((faq, idx) => (
                    <div key={idx} className="border-b border-background-200/60 pb-5 last:border-0">
                      <h3 className="text-base font-semibold text-foreground-900 mb-2">
                        {faq.question}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div>
                  {section.content.split('\n\n').map((paragraph, idx) => (
                    <p
                      key={idx}
                      className="text-sm md:text-base text-foreground-700 leading-relaxed mb-5 last:mb-0"
                    >
                      {paragraph}
                    </p>
                  ))}
                </div>
              )}
            </div>
          </section>
        ))}

        <section className="w-full px-6 md:px-10 py-14 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-8 text-center">
              Related Articles
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.slug}
                  to={`/article/${ra.slug}`}
                  className="group flex flex-col rounded-lg bg-background-100 border border-background-200/60 hover:border-accent-300/50 overflow-hidden transition-all duration-300"
                >
                  <div className="h-36 overflow-hidden">
                    <img
                      src={ra.imageUrl}
                      alt={ra.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="text-xs font-semibold text-foreground-950 leading-snug group-hover:text-accent-700 transition-colors line-clamp-2">
                      {ra.title}
                    </h3>
                    <p className="mt-1.5 text-[11px] text-foreground-400">{ra.date}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="relative w-full h-[400px] md:h-[500px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Clean%20minimalist%20clothing%20store%20interior%20with%20organized%20shelves%20displaying%20folded%20garments%20in%20neutral%20colors%2C%20soft%20ambient%20lighting%2C%20modern%20retail%20design%20with%20wood%20accents%2C%20warm%20inviting%20atmosphere%2C%20boutique%20shopping%20aesthetic&width=1800&height=900&seq=article-cta-v2-2026&orientation=landscape"
              alt="Sugargoo Spreadsheet shopping collection"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-black/40"></div>
          </div>

          <div className="relative z-10 w-full h-full flex flex-col justify-between px-8 md:px-16 py-12 md:py-16">
            <div>
              <h2 className="font-heading text-5xl md:text-7xl font-bold text-white leading-none lowercase">
                start shopping
              </h2>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-6">
              <a
                href={mainSiteUrl}
                {...externalLinkAttrs}
                className="group inline-flex items-center gap-3 px-6 py-3 rounded-full border-2 border-white text-white text-sm md:text-base font-semibold hover:bg-white hover:text-foreground-950 transition-all whitespace-nowrap"
              >
                <span>Buy It Now</span>
                <span className="w-5 h-5 flex items-center justify-center rounded-full bg-white group-hover:bg-foreground-950 transition-colors">
                  <i className="ri-arrow-right-line text-xs text-foreground-950 group-hover:text-white transition-colors"></i>
                </span>
              </a>
              <p className="text-white/60 text-xs md:text-sm leading-relaxed max-w-xs text-right">
                Every find is manually curated.<br />
                All purchases redirect to our trusted main site.
              </p>
            </div>
          </div>
        </section>

        <section className="w-full px-6 md:px-10 py-16 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-8 text-center">
              Explore More Categories
            </h2>
            <div className="flex flex-wrap justify-center gap-3">
              {categories.slice(0, 8).map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  {...externalLinkAttrs}
                  className="px-4 py-2 rounded-full bg-background-50 border border-background-200/60 text-sm text-foreground-600 hover:border-accent-300 hover:text-accent-700 transition-all whitespace-nowrap"
                >
                  {cat.name}
                </a>
              ))}
              <Link
                to="/"
                className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                ← Back to Homepage
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}